import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { Loader } from '@/components/shared/Loader';
import { ListTeamServiceQuery, useListTeamServiceQuery } from '@/graphql';
import {
	PROVIDER_CACHE_KEYS,
	readProviderCache,
	readProviderCacheUpdatedAt,
	writeProviderCache,
} from '@/helpers/providerCache';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

type Services = ListTeamServiceQuery['listTeamServices'];

export interface ServicesContext {
	isLoading: boolean;
	error: Error | null;
	services: Services;
	refetch: () => Promise<any>;
}

const defaultValue: ServicesContext = {
	error: null,
	isLoading: false,
	services: [],
	refetch: async () => {},
};

const ServicesContext = createContext<ServicesContext>(defaultValue);

export function useServices(): ServicesContext {
	const context = useContext(ServicesContext);
	if (isNil(context)) {
		throw new Error(`useServices must be used within a ServicesProvider`);
	}

	return context;
}

export const ServicesProvider: React.FC = ({ children }) => {
	const { enqueueSnackbar } = useSnackbar();
	const cachedServices = readProviderCache<ListTeamServiceQuery>(
		PROVIDER_CACHE_KEYS.services,
	);
	const { data, isLoading, error, refetch, dataUpdatedAt } =
		useListTeamServiceQuery<ListTeamServiceQuery, Error>(
			{},
			{
				initialData: cachedServices ?? undefined,
				initialDataUpdatedAt: readProviderCacheUpdatedAt(
					PROVIDER_CACHE_KEYS.services,
				),
				staleTime: 1000 * 60 * 5, // 5 minutes
				refetchOnMount: true,
				refetchOnWindowFocus: true,
			},
		);

	useEffect(() => {
		if (data) {
			writeProviderCache(PROVIDER_CACHE_KEYS.services, data);
		}
	}, [data]);

	const services = useMemo(() => {
		const safeServices = data?.listTeamServices || [];
		if (!safeServices.length) {
			return safeServices;
		}
		const copiedServices = JSON.parse(
			JSON.stringify(safeServices),
		) as Services;
return copiedServices.sort(
			(a, b) => createServiceDayNumber(a) - createServiceDayNumber(b),
		);
		// We need to re-calculate the services list if the
		// data is ever refetched
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.listTeamServices, dataUpdatedAt]);

	if (error) {
		enqueueSnackbar('Failed to load services', {
			variant: 'warning',
		});
	}

	if (isLoading) {
		return <Loader text="services" />;
	}

	return (
		<ServicesContext.Provider
			value={{
				isLoading,
				services,
				error,
				refetch,
			}}
		>
			{children}
		</ServicesContext.Provider>
	);
};

const createServiceDayNumber = (service: Services[0]): number => {
	try {
		return (
			// Sunday is marked as day 7, but we want Sunday to be first
			// Since Monday is 1, we can make Sunday be 0 so that it becomes
			// first in the list.
			// We multiply the day number by 10,000 because this we we prefix
			// the time with a single digit. 12:45 becomes 1245 and we prefix
			// it with the day number, for example 31245 for Wendesday
			// Monday at 12:45 will be 11245 and therefore it will be sorted
			// before Wednesday. Sunday is turned into 0, so Sunday 12:45 will be
			// 1245 and will always be sorted in the beginning.
			(service.day > 6 ? 0 : service.day) * 10000 +
			parseInt(service.startAt.replace(':', ''))
		);
	} catch (err) {
		return 0;
	}
};

export default ServicesProvider;
